import React, { useState, useEffect } from 'react';
import { 
  FiX, 
  FiUser, 
  FiShield, 
  FiCheckCircle, 
  FiXCircle, 
  FiClock, 
  FiSearch,
  FiFilter 
} from 'react-icons/fi';
import ConsentService from '../../services/consentService';
import { API_BASE_URL, API_DOCTOR_LIST } from '../../../../../helpers/urlHelper';

const ConsentCreationForm = ({ isOpen, onClose, patientId, patientData, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  
  const [grantedToType, setGrantedToType] = useState('');
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [doctors, setDoctors] = useState([]);
  const [centers, setCenters] = useState([]);
  const [activities, setActivities] = useState({});
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [durationType, setDurationType] = useState('permanent');
  const [expirationDate, setExpirationDate] = useState('');
  const [purpose, setPurpose] = useState('');
  const [notes, setNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterModule, setFilterModule] = useState('all');

  const moduleLabels = {
    appointments: 'Citas Médicas',
    prescriptions: 'Recetas Tradicionales',
    electronic_prescriptions: 'Recetas Electrónicas',
    laboratory: 'Análisis de Laboratorio',
    imaging: 'Diagnóstico por Imagen',
    molecular: 'Medicina Molecular',
    vaccinations: 'Vacunaciones',
    treatments: 'Tratamientos',
    medical_records: 'Historias Clínicas'
  };

  useEffect(() => {
    if (isOpen) {
      fetchEntities();
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  const fetchEntities = async () => {
    setLoading(true);
    try {
      const doctorsResponse = await fetch(API_DOCTOR_LIST);
      if (doctorsResponse.ok) {
        const doctorsData = await doctorsResponse.json();
        setDoctors(doctorsData?.data || []);
      }

      const centersResponse = await fetch(`${API_BASE_URL}/healthCenters/clinics`);
      if (centersResponse.ok) {
        const centersData = await centersResponse.json();
        const centersArray = (centersData?.data || centersData || []).map(center => ({
          _id: center._id,
          name: center.name,
          cif: center.cif
        }));
        setCenters(centersArray);
      }
    } catch (error) {
      console.error('Error loading entities:', error);
      setError('Error al cargar doctores y centros');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectEntity = async (entity) => {
    setSelectedEntity(entity);
    setLoading(true);
    setError(null);

    try {
      const result = await ConsentService.getAvailableActivities(patientId, {
        grantedToId: entity._id,
        grantedToType: grantedToType
      });

      if (result.error) {
        setError(result.error.message);
        return;
      }

      setActivities(result.data?.modules || {});
      setStep(2);
    } catch (err) {
      setError('Error al cargar actividades: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const handleSelectActivity = (moduleType, activity) => {
    setSelectedActivity({
      moduleType,
      activity
    });
  };

  const handleSubmit = async () => {
    if (!purpose.trim()) {
      setError('El propósito del consentimiento es obligatorio');
      return;
    }
    if (!selectedActivity) {
      setError('Debes seleccionar una actividad');
      return;
    }
    if (durationType === 'temporary' && !expirationDate) {
      setError('Debes especificar una fecha de expiración');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { moduleType, activity } = selectedActivity;
      
      const moduleConsents = [{
        module: moduleType,
        fullConsent: false,
        specificElements: [{
          elementId: activity.element_id,
          elementType: moduleType,
          consentGiven: true,
          metadata: {
            description: activity.description || '',
            date: activity.date || new Date(),
            code: activity.code || ''
          }
        }],
        allowedActions: ['view', 'download']
      }];

      const consentData = {
        patientId,
        grantedToType,
        grantedToId: selectedEntity._id,
        moduleConsents,
        durationType,
        expiresOn: durationType === 'temporary' ? expirationDate : null,
        purpose,
        notes,
        verificationMethod: 'password'
      };

      const result = await ConsentService.createConsent(consentData);

      if (result.error) {
        setError(result.error.message);
        return;
      }

      setShowSuccessAlert(true);
      
      setTimeout(() => {
        setShowSuccessAlert(false);
        resetForm();
        if (onSuccess) onSuccess();
        onClose();
      }, 2000);

    } catch (err) {
      setError('Error al crear consentimiento: ' + (err.message || 'Error desconocido'));
    } finally {
      setLoading(false);
    }
  };

  const getFilteredActivities = () => {
    if (!activities) return {};
    let filtered = { ...activities };
    if (filterModule !== 'all') {
      filtered = { [filterModule]: filtered[filterModule] };
    }
    if (searchTerm) {
      Object.keys(filtered).forEach(moduleType => {
        filtered[moduleType] = filtered[moduleType].filter(activity =>
          activity.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          activity.code.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
    }
    return filtered;
  };

  const resetForm = () => {
    setStep(1);
    setGrantedToType('');
    setSelectedEntity(null);
    setActivities({});
    setSelectedActivity(null);
    setDurationType('permanent');
    setExpirationDate('');
    setPurpose('');
    setNotes('');
    setSearchTerm('');
    setFilterModule('all');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="offcanvas-backdrop fade show" onClick={handleClose}></div>
      
      <div className="offcanvas offcanvas-end show w-50" tabIndex="-1">
        <div className="offcanvas-header bg-primary text-white">
          <div>
            <h5 className="offcanvas-title mb-1">Crear Nuevo Consentimiento</h5>
            <small className="opacity-75">Paso {step} de 3</small>
          </div>
          <button
            type="button"
            className="btn-close btn-close-white"
            onClick={handleClose}
          ></button>
        </div>

        <div className="px-4 py-3 bg-light border-bottom">
          <div className="d-flex gap-2">
            {[1, 2, 3].map((s) => (
              <div key={s} className="flex-fill">
                <div className="progress" role="progressbar">
                  <div className={`progress-bar ${step >= s ? 'bg-primary' : 'bg-secondary'} w-100`}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {showSuccessAlert && (
          <div className="position-fixed top-50 start-50 translate-middle" style={{zIndex: 10000}}>
            <div className="alert alert-success d-flex align-items-center shadow-lg animate__animated animate__fadeIn" role="alert">
              <FiCheckCircle className="me-3 fs-1" />
              <div>
                <h6 className="mb-1 fw-bold">¡Consentimiento Creado!</h6>
                <small>Se ha otorgado acceso correctamente</small>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="m-4 alert alert-danger d-flex align-items-start">
            <FiXCircle className="me-2 mt-1" />
            <span>{error}</span>
          </div>
        )}

        <div className="offcanvas-body overflow-auto">
          {step === 1 && (
            <div>
              <h6 className="mb-3">¿A quién deseas otorgar acceso?</h6>
              
              <div className="row g-3 mb-4">
                <div className="col-6">
                  <button
                    onClick={() => setGrantedToType('doctor')}
                    className={`btn w-100 p-4 ${grantedToType === 'doctor' ? 'btn-primary' : 'btn-outline-primary'}`}
                  >
                    <FiUser className="d-block mx-auto mb-2 fs-1" />
                    <h6 className="mb-1">Doctor</h6>
                    <small>Acceso selectivo</small>
                  </button>
                </div>

                <div className="col-6">
                  <button
                    onClick={() => setGrantedToType('center')}
                    className={`btn w-100 p-4 ${grantedToType === 'center' ? 'btn-primary' : 'btn-outline-primary'}`}
                  >
                    <FiShield className="d-block mx-auto mb-2 fs-1" />
                    <h6 className="mb-1">Centro</h6>
                    <small>Acceso general</small>
                  </button>
                </div>
              </div>

              {grantedToType && (
                <div>
                  <h6 className="mb-3">
                    Selecciona {grantedToType === 'doctor' ? 'un doctor' : 'un centro'}:
                  </h6>
                  <div className="overflow-auto" style={{maxHeight: '400px'}}>
                    <div className="list-group">
                      {(grantedToType === 'doctor' ? doctors : centers).map(entity => (
                        <button
                          key={entity._id}
                          onClick={() => handleSelectEntity(entity)}
                          disabled={loading}
                          className="list-group-item list-group-item-action"
                        >
                          <div className="fw-bold">
                            {entity.name} {entity.surname || ''}
                          </div>
                          {entity.specialty && (
                            <small className="text-muted d-block">{entity.specialty}</small>
                          )}
                          {entity.email && (
                            <small className="text-muted d-block">{entity.email}</small>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h6 className="mb-0">Selecciona una Actividad</h6>
                  <small className="text-muted">
                    {selectedEntity?.name} {selectedEntity?.surname}
                  </small>
                </div>
                {selectedActivity && (
                  <div className="text-end">
                    <span className="badge bg-success fs-6">✓ Seleccionada</span>
                  </div>
                )}
              </div>

              <div className="row g-2 mb-3">
                <div className="col-8">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text">
                      <FiSearch />
                    </span>
                    <input
                      type="text"
                      placeholder="Buscar..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="form-control"
                    />
                  </div>
                </div>
                <div className="col-4">
                  <select
                    value={filterModule}
                    onChange={(e) => setFilterModule(e.target.value)}
                    className="form-select form-select-sm"
                  >
                    <option value="all">Todos</option>
                    {Object.keys(activities).map(moduleType => (
                      <option key={moduleType} value={moduleType}>
                        {moduleLabels[moduleType]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="overflow-auto" style={{maxHeight: '400px'}}>
                {Object.entries(getFilteredActivities()).map(([moduleType, moduleActivities]) => {
                  if (!moduleActivities || moduleActivities.length === 0) return null;
                  const availableActivities = moduleActivities.filter(a => a.is_consentable);
                  
                  return (
                    <div key={moduleType} className="card mb-2">
                      <div className="card-header bg-light py-2">
                        <div className="fw-bold">
                          {moduleLabels[moduleType]}
                          <small className="text-muted d-block">
                            {availableActivities.length} disponibles
                          </small>
                        </div>
                      </div>

                      <div className="list-group list-group-flush">
                        {moduleActivities.map(activity => {
                          const isSelected = selectedActivity?.activity?.element_id === activity.element_id;
                          
                          return (
                            <div
                              key={activity.element_id}
                              className={`list-group-item ${activity.is_already_consented ? 'bg-light' : ''} ${isSelected ? 'border-primary border-2' : ''}`}
                            >
                              <div className="form-check">
                                <input
                                  type="radio"
                                  name="selectedActivity"
                                  checked={isSelected}
                                  onChange={() => handleSelectActivity(moduleType, activity)}
                                  disabled={activity.is_already_consented}
                                  className="form-check-input"
                                  id={`activity-${activity.element_id}`}
                                />
                                <label className="form-check-label" htmlFor={`activity-${activity.element_id}`}>
                                  <div className="fw-medium">{activity.description}</div>
                                  <div className="d-flex gap-2 mt-1">
                                    <small className="text-muted">
                                      <FiClock size={12} className="me-1" />
                                      {new Date(activity.date).toLocaleDateString('es-ES')}
                                    </small>
                                    <small className="badge bg-secondary">
                                      {activity.code}
                                    </small>
                                  </div>
                                  {activity.is_already_consented && (
                                    <small className="text-warning d-block mt-1">
                                      Ya consentido
                                    </small>
                                  )}
                                </label>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="d-flex gap-2 mt-3">
                <button
                  onClick={() => setStep(1)}
                  className="btn btn-outline-secondary"
                >
                  Atrás
                </button>
                <button
                  onClick={() => setStep(3)}
                  disabled={!selectedActivity}
                  className="btn btn-primary flex-fill"
                >
                  Continuar
                </button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h6 className="mb-3">Configurar Consentimiento</h6>

              {selectedActivity && (
                <div className="alert alert-info mb-3">
                  <strong>Actividad seleccionada:</strong>
                  <div className="mt-1">{selectedActivity.activity.description}</div>
                  <small className="text-muted">
                    {moduleLabels[selectedActivity.moduleType]} - {selectedActivity.activity.code}
                  </small>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label fw-bold">Duración</label>
                <div className="btn-group w-100" role="group">
                  <input
                    type="radio"
                    className="btn-check"
                    name="durationType"
                    id="duration-permanent"
                    checked={durationType === 'permanent'}
                    onChange={() => setDurationType('permanent')}
                  />
                  <label className="btn btn-outline-primary" htmlFor="duration-permanent">
                    Permanente
                  </label>

                  <input
                    type="radio"
                    className="btn-check"
                    name="durationType"
                    id="duration-temporary"
                    checked={durationType === 'temporary'}
                    onChange={() => setDurationType('temporary')}
                  />
                  <label className="btn btn-outline-primary" htmlFor="duration-temporary">
                    Temporal
                  </label>
                </div>
                
                {durationType === 'temporary' && (
                  <input
                    type="date"
                    value={expirationDate}
                    onChange={(e) => setExpirationDate(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    className="form-control mt-2"
                  />
                )}
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">
                  Propósito <span className="text-danger">*</span>
                </label>
                <textarea
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder="Ej: Seguimiento médico, segunda opinión..."
                  rows={3}
                  className="form-control"
                />
              </div>

              <div className="mb-3">
                <label className="form-label fw-bold">Notas (Opcional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={2}
                  className="form-control"
                />
              </div>

              <div className="d-flex gap-2">
                <button
                  onClick={() => setStep(2)}
                  className="btn btn-outline-secondary"
                >
                  Atrás
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !purpose.trim()}
                  className="btn btn-primary flex-fill"
                >
                  {loading ? 'Creando...' : 'Crear Consentimiento'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {loading && (
        <div className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50" style={{zIndex: 9999}}>
          <div className="bg-white rounded p-4 d-flex align-items-center gap-3">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <span className="fw-medium">Cargando...</span>
          </div>
        </div>
      )}
    </>
  );
};

export default ConsentCreationForm;